import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormState } from 'react-use-form-state';
import { Input, Label, Form, FormGroup, FormFeedback } from 'reactstrap';
import { Button } from '@shoflo/polymer/components/Button/Button';
import { all } from 'underscore';

import {
  recordingSettings,
  getDefaultEndpointEnvSettings
} from '@shoflo/polymer/components/RecordForm/RecordingSettings';
import { StyledLink, StyledSubHeader } from './StreamEndpointForm.styled';
import StreamEndpointPropType from './StreamEndpointPropType';

// Stream endpoint fields that needs validation
const requiredFields = ['title', 'uri', 'streamKey'];
const rtmpUriRegex =
  /^rtmps?:\/\/[a-zA-Z0-9._-]+(:[0-9]+)?(\/[a-zA-Z0-9._-]*)?(\/[^\s?#]*)?(\?[^\s#]*)?(#[^\s]*)?$/;

const StreamEndpointForm = ({ onSubmit, onCancel, endpoint }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(!!endpoint);

  const envSettings = endpoint
    ? extractSettings(endpoint.env)
    : getDefaultEndpointEnvSettings();

  const { title = '', uri = '', streamKey = '' } = endpoint || {};
  const addOrSaveButtonText = endpoint ? 'Save' : 'Create';
  const isSubmittingText = endpoint ? 'Updating...' : 'Creating...';

  const [formState, { text }] = useFormState({
    title,
    uri,
    streamKey,
    ...envSettings
  });

  useEffect(() => {
    if (isEditing) {
      requiredFields.forEach(key => {
        formState.setField(key, endpoint[key]);
      });
      setIsEditing(false);
    }
  }, [isEditing, formState, endpoint]);

  const isFormValid = checkIsFormValid(formState, endpoint);

  return (
    <div>
      <Form
        data-test="new-endpoint-form"
        onSubmit={e => {
          e.preventDefault();
          setIsSubmitting(true);

          const streamEndpoint = {
            title: formState.values.title.trim(),
            uri: formState.values.uri.trim(),
            streamKey: formState.values.streamKey.trim(),
            env: {
              SCREEN_WIDTH: formState.values.VIDEO_RESOLUTION.split('x')[0],
              SCREEN_HEIGHT: formState.values.VIDEO_RESOLUTION.split('x')[1]
            }
          };

          onSubmit(streamEndpoint);
        }}
      >
        <StyledSubHeader>
          Stream URLs and Stream keys for most streaming sites can be found in
          their respective settings pages.
          <StyledLink
            href="https://learn.shoflo.tv/en/articles/4640588-streaming-to-rtmp-endpoints"
            target="_blank"
          >
            Learn more
          </StyledLink>
        </StyledSubHeader>
        <FormGroup>
          <Label id="streamEndpointTitleLabel" htmlFor="streamEndpointTitle">
            Stream title
          </Label>
          <Input
            data-testid="streamEndpointTitleInput"
            id="streamEndpointTitle"
            aria-labelledby="streamEndpointTitleLabel"
            placeholder="e.g. My Primary Stream"
            {...text({
              name: 'title',
              validate: t => {
                return t.trim().length >= 1;
              },
              validateOnBlur: false,
              touchOnChange: true
            })}
            invalid={formState.touched.title && !formState.validity.title}
            required
          />
          <FormFeedback>Uh oh! A title is required.</FormFeedback>
        </FormGroup>

        <FormGroup>
          <Label id="streamEndpointURILabel" htmlFor="streamEndpointURI">
            Stream RTMP URL
          </Label>
          <Input
            data-testid="streamEndpointURIInput"
            id="streamEndpointURI"
            aria-labelledby="streamEndpointURILabel"
            placeholder="e.g. RTMP://streamURL"
            {...text({
              name: 'uri',
              validate: t => rtmpUriRegex.test(t.trim()),
              validateOnBlur: false,
              touchOnChange: true
            })}
            invalid={formState.touched.uri && !formState.validity.uri}
            required
          />
          <FormFeedback>Uh oh! A valid RTMP URL is required.</FormFeedback>
        </FormGroup>

        <FormGroup>
          <Label
            id="streamEndpointStreamKeyLabel"
            htmlFor="streamEndpointStreamKey"
          >
            Stream key
          </Label>
          <Input
            data-testid="streamEndpointStreamKeyInput"
            id="streamEndpointStreamKey"
            aria-labelledby="streamEndpointStreamKeyLabel"
            placeholder="e.g. ABCxyz123"
            {...text({
              name: 'streamKey',
              validate: t => {
                return t.trim().length >= 1;
              },
              validateOnBlur: false,
              touchOnChange: true
            })}
            invalid={
              formState.touched.streamKey && !formState.validity.streamKey
            }
            required
          />
          <FormFeedback>Uh oh! A stream key is required.</FormFeedback>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="videoResolution">Video resolution</Label>
          <Input
            type="select"
            onChange={e => {
              formState.setField('VIDEO_RESOLUTION', e.target.value);
            }}
            id="videoResolution"
            value={formState.values.VIDEO_RESOLUTION}
          >
            {recordingSettings.videoResolution.map(item => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </Input>
        </FormGroup>

        <div className="float-end">
          <Button
            data-testid="createButton"
            disabled={!isFormValid || isSubmitting}
            data-dd-action-name="Create Stream"
          >
            {isSubmitting ? isSubmittingText : addOrSaveButtonText}
          </Button>{' '}
          <Button
            data-test="cancelButton"
            data-dd-action-name="Cancel create stream"
            onClick={e => {
              e.preventDefault();
              onCancel();
            }}
            color="light"
            variant="outlined"
          >
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
};

function checkIsFormValid(formState) {
  const { validity, values } = formState;

  return all(requiredFields, field => {
    return values[field] && validity[field];
  });
}

StreamEndpointForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  endpoint: StreamEndpointPropType
};

export default StreamEndpointForm;

// TODO: Move to utils folder - Tech debt ref: WEBINAR-15795
export const extractSettings = env => ({
  VIDEO_RESOLUTION: `${env.SCREEN_WIDTH}x${env.SCREEN_HEIGHT}` // 1920x1080
});
